<template>
  <nav class="flex" aria-label="Breadcrumb">
    <ol class="inline-flex items-center space-x-1 md:space-x-3">
      <li v-for="(item, index) in breadcrumbs" :key="index">
        <div class="flex items-center">
          <span 
            :class="[
              'text-sm font-medium',
              index === breadcrumbs.length - 1 
                ? 'text-gray-500' 
                : 'text-gray-700 hover:text-blue-600 cursor-pointer'
            ]"
            @click="index < breadcrumbs.length - 1 && navigateToBreadcrumb(item)"
          >
            {{ item.name }}
          </span>
          <ChevronRight v-if="index < breadcrumbs.length - 1" class="w-4 h-4 text-gray-400 mx-1" />
        </div>
      </li>
    </ol>
  </nav>
</template>

<script>
import { computed, inject } from 'vue'
import { useRouter } from 'vue-router'
import { ChevronRight } from 'lucide-vue-next'

export default {
  name: 'Breadcrumb',
  components: {
    ChevronRight
  },
  props: {
    currentView: {
      type: String,
      required: true
    },
    currentTab: {
      type: String,
      default: null
    }
  },
  setup(props) {
    const router = useRouter()
    const auth = inject('auth')
    const breadcrumbMap = {
      'dashboard': [{ name: 'Dashboard' }],
      'tasks': [{ name: 'Dashboard' }, { name: 'My Tasks' }],
      'manager-tasks': [{ name: 'Dashboard' }, { name: 'Task Management' }],
      'admin-tasks': [{ name: 'Dashboard' }, { name: 'Task Administration' }],
      'teams': [{ name: 'Dashboard' }, { name: 'Teams' }],
      'organization': [{ name: 'Dashboard' }, { name: 'Organization' }],
      'profile': [{ name: 'Dashboard' }, { name: 'Profile Settings' }],
      'create-user': [{ name: 'Dashboard' }, { name: 'Create User' }],
      'charts': [{ name: 'Dashboard' }, { name: 'Charts' }],
      'schedule': [{ name: 'Dashboard' }, { name: 'Schedule' }],
      'payroll': [{ name: 'Dashboard' }, { name: 'Payroll' }],
    }

    const dashboardTabMap = {
      'overview': 'Overview',
      'leave-management': 'Leave Management',
      'task-management': 'Task Management',
      'user-management': 'User Management',
      'management': 'Management Tools',
      'analytics': 'Analytics',
      'reports': 'Reports'
    }

    const breadcrumbs = computed(() => {
      let breadcrumb = breadcrumbMap[props.currentView] || [{ name: 'Unknown' }]
      
      // If we're in dashboard and have a current tab, add it
      if (props.currentView === 'dashboard' && props.currentTab) {
        breadcrumb = [
          { name: 'Dashboard' },
          { name: dashboardTabMap[props.currentTab] || props.currentTab }
        ]
      }
      
      // If we're in profile settings and have a current tab, add it
      if (props.currentView === 'profile' && props.currentTab) {
        const tabNames = {
          'personal': 'Personal',
          'working-times': 'Working Times',
          'accessibility': 'Accessibility',
          'security': 'Security',
          'account': 'Account'
        }
        breadcrumb = [
          { name: 'Dashboard' },
          { name: 'Profile Settings' },
          { name: tabNames[props.currentTab] || props.currentTab }
        ]
      }
      
      return breadcrumb
    })

    const navigateToBreadcrumb = (item) => {
      if (item.name === 'Dashboard') {
        // All users go to /dashboard - AppContent handles role-based component rendering
        router.push('/dashboard')
      } else if (item.name === 'Profile Settings') {
        router.push('/profile')
      } else if (item.name === 'Teams') {
        router.push('/teams')
      } else if (item.name === 'Schedule') {
        router.push('/schedule')
      } else if (item.name === 'Payroll') {
        router.push('/payroll')
      } else if (item.name === 'Organization') {
        router.push('/organization')
      } else if (item.name === 'Create User') {
        router.push('/create-user')
      } else if (item.name === 'My Tasks') {
        router.push('/tasks')
      } else if (item.name === 'Task Management') {
        router.push('/manager-tasks')
      } else if (item.name === 'Task Administration') {
        router.push('/admin-tasks')
      } else if (item.name === 'Charts') {
        router.push('/charts')
      }
      // For dashboard tabs, we don't navigate away, just stay on dashboard
      // The tab change will be handled by the dashboard component
    }

    return {
      breadcrumbs,
      navigateToBreadcrumb
    }
  }
}
</script>
